import {FunctionComponent, useCallback, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router';

import Button from '@/components/button';
import ConfirmDialog from '@/components/confirm-dialog';
import Spinner from '@/components/spinner';
import {BUTTON_THEME, BUTTON_VARIANT} from '@/constants/button';
import {useAllocations, useDeleteAllocation} from '@/hooks/queries/allocations';
import {Toaster} from '@/initializers/toaster';
import {TrackingClickPrefix} from '@/services/analytics/analytics-types';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';
import {useConfirmDialogActions} from '../confirm-dialog/use-confirm-dialog-actions';
import {AllocationRow} from './allocation-row';

type Props = {
  ['data-testid']?: string;
};

const AdvisorPredeterminedAllocations: FunctionComponent<Props> = ({...props}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const allocations = useAllocations();
  const deleteAllocation = useDeleteAllocation();

  const [shouldConfirmDeletionForAllocationId, setShouldConfirmDeletionForAllocationId] = useState<string | null>(null);

  const startAllocationCreation = useCallback(() => {
    navigate('/app/profile/allocations/create');
  }, [navigate]);

  const startAllocationDeletion = useCallback((allocationId: string) => {
    setShouldConfirmDeletionForAllocationId(allocationId);
  }, []);

  const cancelAllocationDeletion = useCallback(() => {
    setShouldConfirmDeletionForAllocationId(null);
  }, []);

  const confirmAllocationDeletion = useCallback(async () => {
    if (!shouldConfirmDeletionForAllocationId) return;

    try {
      await deleteAllocation.mutateAsync(shouldConfirmDeletionForAllocationId, {
        onSuccess() {
          queryClient.invalidateQueries({queryKey: ['allocations']});
        },
      });
    } catch (_error) {
      Toaster.error(t('constants.feedback-message.default.error'));
    } finally {
      setShouldConfirmDeletionForAllocationId(null);
    }
  }, [deleteAllocation, queryClient, t, shouldConfirmDeletionForAllocationId]);

  const confirmationDialogActions = useConfirmDialogActions({
    onCancel: cancelAllocationDeletion,
    onConfirm: confirmAllocationDeletion,
  });

  return (
    <div {...props}>
      <div className="mt-4 grid grid-cols-6 items-center justify-between rounded-t-md bg-violet px-4 py-3">
        <div className="col-span-3 font-bold text-white">
          {t('components.advisor-predetermined-allocations.predetermined-allocations.name')}
        </div>

        <div className="col-span-2 font-bold text-white">
          {t('components.advisor-predetermined-allocations.predetermined-allocations.contract-type')}
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            theme={BUTTON_THEME.TERTIARY}
            variant={BUTTON_VARIANT.SMALL}
            label={t('components.advisor-predetermined-allocations.add')}
            onClick={startAllocationCreation}
            trackingKey={WEB_CLICK_TRACKING_KEY.ADD_ALLOCATION}
            trackingAs={TrackingClickPrefix.NAV}
          />
        </div>
      </div>

      <div className="rounded-b-md border border-grey-400">
        {allocations.isLoading ? (
          <div className="flex min-h-96 w-full items-center justify-center">
            <Spinner />
          </div>
        ) : allocations.data?.data.length && allocations.data?.data.length > 0 ? (
          allocations.data?.data.map(allocation => (
            <AllocationRow
              key={allocation.id}
              allocation={allocation}
              onRowDeleteClick={event => {
                event.preventDefault();
                event.stopPropagation();
                startAllocationDeletion(allocation.id);
              }}
            />
          ))
        ) : (
          <div className="flex items-center justify-center p-8">
            {t('components.advisor-predetermined-allocations.no-allocations')}
          </div>
        )}
      </div>

      <ConfirmDialog
        isVisible={!!shouldConfirmDeletionForAllocationId}
        actions={confirmationDialogActions}
        title={t('components.advisor-predetermined-allocations.deletion-confirm.title')}
        text={t('components.advisor-predetermined-allocations.deletion-confirm.text')}
      />
    </div>
  );
};

export default AdvisorPredeterminedAllocations;
